import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { defineMessages } from "react-intl";
import { BottomSheet } from "react-spring-bottom-sheet";

import { useFormatMessage } from "../Intl/Context";
import { Desktop } from "../Utils/ResponsiveQueries";
import Button from "../Buttons/Button";
import Alert from "../Alerts/Alert";

const BottomSheetPanelContext = createContext({});

export const useBottomSheetPanelContext = () => {
  return useContext(BottomSheetPanelContext);
};

// Ensure that the maximum height don't cover all the screen so it keeps a visible part of
// the background, to keep the context of being in a panel and not a full page.
export const MAX_HEIGHT_RATIO = 0.9;

// Snap to the smaller of the content's natural height or 85% of the viewport,
// so shorter cards don't leave empty white space at the bottom.
const defaultSnapPoints = ({ maxHeight, minHeight }) => [
  Math.min(minHeight, maxHeight * MAX_HEIGHT_RATIO)
];

// Wrapper around `react-spring-bottom-sheet` that adds a context provider
// exposing the scrollable content ref and two footer slots:
// - `setActionsFooterContent`: for contribution interaction buttons (votes, comment)
// - `setNavFooterContent`: for navigation buttons (previous, next)
// Both slots are composed into the library's `footer` prop so they render
// outside the scroll area and stay visible regardless of scroll position.
//
const BottomSheetPanel = ({
  open,
  onDismiss,
  title,
  children,
  className,
  trapFocus = true
}) => {
  const sheetRef = useRef();
  const contentRef = useRef();
  const rootElRef = useRef();

  const [actionsFooterContent, setActionsFooterContent] = useState(null);
  const [navFooterContent, setNavFooterContent] = useState(null);

  // Compose both slots into a single footer node. The library requires a
  // non-null value to render the footer container.
  const footer =
    actionsFooterContent || navFooterContent ? (
      <>
        {actionsFooterContent}
        {navFooterContent}
      </>
    ) : null;

  // Capture the library's internal scroll container (`[data-rsbs-scroll]`)
  // after the sheet finishes its open animation, so consumers can call
  // `contentRef.current.scrollTo(...)`.
  const handleSpringEnd = useCallback(event => {
    // Only capture the scroll container on the initial open, not on subsequent
    // animations.
    if (event.type !== "OPEN" || contentRef.current || !rootElRef.current) return;

    contentRef.current = rootElRef.current.querySelector("[data-rsbs-scroll]");
  }, []);

  // Grab the root DOM element on mount via the `[data-rsbs-root]` attribute
  // rendered by the library. We use a short delay to wait for the portal to
  // render.
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      rootElRef.current = document.querySelector("[data-rsbs-root]");
    }, 50);

    return () => clearTimeout(timer);
  }, [open]);

  // Re-snap the sheet to fit the current content height. Deferred to the
  // next frame so React has time to render the new content before the
  // library measures it.
  const snapToContent = useCallback(() => {
    requestAnimationFrame(() => {
      if (!sheetRef.current) return;

      sheetRef.current.snapTo(({ minHeight, maxHeight }) =>
        Math.min(minHeight, maxHeight * MAX_HEIGHT_RATIO)
      );
    });
  }, []);

  const t = useFormatMessage();
  const closeLabel = t(intlMessages.close);

  const header = useMemo(() => {
    if (!title) return null;

    return (
      <div className="bottom-sheet-panel-header">
        <span className="bottom-sheet-panel-title">{title}</span>

        <Desktop>
          <Button
            type="button"
            className="bottom-sheet-panel-close"
            onClick={onDismiss}
            iconClassName="icon-close"
            screenReaderLabel={closeLabel}
          />
        </Desktop>
      </div>
    );
  }, [title, onDismiss, closeLabel]);

  return (
    <BottomSheetPanelContext.Provider
      value={{
        rootElRef,
        contentRef,
        snapToContent,
        setActionsFooterContent,
        setNavFooterContent
      }}
    >
      <BottomSheet
        ref={sheetRef}
        open={open}
        onDismiss={onDismiss}
        blocking={trapFocus}
        header={header}
        footer={footer}
        snapPoints={defaultSnapPoints}
        onSpringEnd={handleSpringEnd}
        className={className}
      >
        {/* Make the alerts be displayed on the top of the screen in the root container, 
            as we can't display it inside the bottom sheet as it would be contained in
            the sheet container. Also we need to display the alert here to ensure that 
            we can interact with it. */}
        {rootElRef.current && createPortal(<Alert />, rootElRef.current)}

        {children}
      </BottomSheet>
    </BottomSheetPanelContext.Provider>
  );
};

export default BottomSheetPanel;

const intlMessages = defineMessages({
  close: {
    id: "bottomSheetPanel.close",
    defaultMessage: "Fermer le panneau"
  }
});
